import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
 Badge,
 Box,
 Button,
 Flex,
 Grid,
 Heading,
 Input,
 Table,
 TableContainer,
 Tbody,
 Td,
 Text,
 Th,
 Thead,
 Tooltip,
 Tr,
 useToast,
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { fetchData } from '../../services/fetch';
import baseService from '../../services/base';
import { FilterCombobox, Select } from '../../components/Input';
import Loader from '../../components/Feedback/Loader';
import { Pagination } from '../../components/Pagination';

const PAGE_SIZE = 10;

const TYPE_LABELS = {
 simple: 'Simples',
 diagnostic: 'Diagnóstico',
 periodic: 'Periódico',
 certification: 'Certificação',
};

const TYPE_COLORS = {
 simple: 'blue',
 diagnostic: 'purple',
 periodic: 'orange',
 certification: 'green',
};

const EMPTY_FILTERS = {
 type: '',
 company: '',
 questionnaire: '',
 deadlineFrom: '',
 deadlineTo: '',
 status: '',
 auditorId: '',
};

function formatDeadline(value) {
 if (!value) return '--';
 try {
  return format(parseISO(value), 'dd/MM/yyyy');
 } catch {
  return '--';
 }
}

export default function Audit() {
 const toast = useToast();
 const navigate = useNavigate();

 // Filtros vivem na URL, não em state solto — voltar da tela de auditoria
 // (que faz `navigate('/audit')`, remontando este componente do zero) não
 // pode apagar o que o auditor tinha selecionado.
 const [searchParams, setSearchParams] = useSearchParams();
 const [savingRowId, setSavingRowId] = useState(null);
 const [page, setPage] = useState(1);

 const filters = useMemo(
  () => ({ ...EMPTY_FILTERS, ...Object.fromEntries(searchParams.entries()) }),
  [searchParams]
 );

 const queryString = searchParams.toString();

 // Trocar de filtro pode deixar a página atual sem linhas — volta pra 1ª.
 useEffect(() => {
  setPage(1);
 }, [queryString]);

 const {
  data: rows,
  isLoading,
  error,
  mutate,
 } = useSWR(`audit/questionnaires${queryString ? `?${queryString}` : ''}`, fetchData);

 // Paginação client-side: o endpoint já devolve a lista inteira filtrada (a
 // query é uma UNION com dedup entre survey e certificado, sem LIMIT/OFFSET no
 // backend). Volume atual é pequeno o bastante pra paginar em memória.
 const totalRows = rows?.length || 0;
 const lastPage = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
 const pagedRows = useMemo(
  () => (rows || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
  [rows, page]
 );

 const { data: auditors } = useSWR('audit/auditors', fetchData);

 // Opções vêm do backend sem filtro aplicado: se saíssem das linhas já
 // filtradas, escolher um filtro apagaria as opções dos outros.
 const { data: filterOptions } = useSWR('audit/filters', fetchData);
 const options = filterOptions || { statuses: [], companies: [], questionnaires: [] };

 // O e-mail entra como descrição para diferenciar homônimos — o combobox também
 // busca por ele.
 const auditorOptions = useMemo(
  () =>
   (auditors || []).map((auditor) => ({
    value: auditor.id_user,
    label: auditor.username,
    description: auditor.email,
   })),
  [auditors]
 );

 const activeFilterCount = Object.values(filters).filter((value) => value !== '').length;
 const hasActiveFilter = activeFilterCount > 0;

 function updateFilter(key, value) {
  const next = new URLSearchParams(searchParams);
  if (value === '') next.delete(key);
  else next.set(key, value);
  setSearchParams(next, { replace: true });
 }

 function openAudit(row) {
  const params = new URLSearchParams({
   kind: row.kind,
   survey: String(row.audit_survey_id),
   company: String(row.id_company),
   type: row.type,
   pillar: String(!!row.has_pillar),
   name: row.name || '',
   status: row.status || '',
  });
  // `control` é genérico: id do certificate_status_control OU do
  // diagnostic_status_control, dependendo do tipo — nunca os dois juntos.
  const controlId = row.id_certificate_status_control || row.id_diagnostic_status_control;
  if (controlId) {
   params.set('control', String(controlId));
  }
  // Mesma aba de propósito: em aba nova o SSO recarrega do zero e, enquanto
  // `isAuthLoading`, as rotas por papel ainda não existem — o catch-all jogava
  // o auditor em /registration.
  navigate(`/audit/view?${params.toString()}`);
 }

 async function assignAuditor(row, idUser) {
  setSavingRowId(row.id);
  try {
   await baseService.put('audit/assignment', {
    id_survey: row.id_survey ?? undefined,
    id_certificate: row.id_certificate ?? undefined,
    id_company: row.id_company,
    id_user: idUser,
   });
   await mutate();
  } catch {
   toast({ title: 'Não foi possível alterar o auditor', status: 'error' });
  } finally {
   setSavingRowId(null);
  }
 }

 return (
  <Box>
   <Heading as='h1' fontSize='24px' mb='4px'>
    Auditoria
   </Heading>
   <Text color='#818A8F' fontSize='14px' mb='24px'>
    Todos os questionários vinculados a empresas.
   </Text>

   <Box
    bg='white'
    border='1px solid #EAEEF0'
    borderRadius='12px'
    boxShadow='0 1px 2px rgba(16, 24, 32, 0.04)'
    p='16px'
    mb='24px'
   >
    <Flex justify='space-between' align='center' mb='14px'>
     <Flex align='center' gap='8px'>
      <Text fontWeight={700} fontSize='14px' color='#16262E'>
       Filtros
      </Text>
      {hasActiveFilter && (
       <Badge colorScheme='blue' borderRadius='full' px='8px' fontWeight={600}>
        {activeFilterCount} {activeFilterCount === 1 ? 'ativo' : 'ativos'}
       </Badge>
      )}
     </Flex>

     <Button
      variant='ghost'
      size='sm'
      onClick={() => setSearchParams({}, { replace: true })}
      isDisabled={!hasActiveFilter}
     >
      Limpar filtros
     </Button>
    </Flex>

    <Grid templateColumns='repeat(auto-fit, minmax(160px, 1fr))' gap='12px'>
     <Select
      name='type'
      label='Tipo'
      value={filters.type}
      onChange={(e) => updateFilter('type', e.target.value)}
     >
      <option value=''>Todos</option>
      {Object.entries(TYPE_LABELS).map(([value, label]) => (
       <option key={value} value={value}>
        {label}
       </option>
      ))}
     </Select>

     <FilterCombobox
      label='Empresa'
      value={filters.company}
      options={options.companies}
      onChange={(value) => updateFilter('company', value)}
      searchPlaceholder='Buscar empresa'
      emptyLabel='Nenhuma empresa encontrada'
     />

     <FilterCombobox
      label='Questionário'
      value={filters.questionnaire}
      options={options.questionnaires}
      onChange={(value) => updateFilter('questionnaire', value)}
      searchPlaceholder='Buscar questionário'
      emptyLabel='Nenhum questionário encontrado'
     />

     <Box>
      <Text fontSize='xs' color='#818A8F' mb='8px'>
       Prazo de
      </Text>
      <Input
       type='date'
       value={filters.deadlineFrom}
       onChange={(e) => updateFilter('deadlineFrom', e.target.value)}
       bg='white'
      />
     </Box>

     <Box>
      <Text fontSize='xs' color='#818A8F' mb='8px'>
       Prazo até
      </Text>
      <Input
       type='date'
       value={filters.deadlineTo}
       onChange={(e) => updateFilter('deadlineTo', e.target.value)}
       bg='white'
      />
     </Box>

     <Select
      name='status'
      label='Status'
      value={filters.status}
      onChange={(e) => updateFilter('status', e.target.value)}
     >
      <option value=''>Todos</option>
      {options.statuses.map((status) => (
       <option key={status} value={status}>
        {status}
       </option>
      ))}
     </Select>

     <Select
      name='auditorId'
      label='Auditor vinculado'
      value={filters.auditorId}
      onChange={(e) => updateFilter('auditorId', e.target.value)}
     >
      <option value=''>Todos</option>
      {(auditors || []).map((auditor) => (
       <option key={auditor.id_user} value={auditor.id_user}>
        {auditor.username}
       </option>
      ))}
     </Select>
    </Grid>
   </Box>

   {error && <Text color='red.600'>Erro ao carregar os questionários.</Text>}

   {isLoading ? (
    <Loader />
   ) : (
    <TableContainer bg='white' borderRadius='12px' p='8px'>
     <Table variant='simple'>
      <Thead>
       <Tr>
        <Th>Tipo</Th>
        <Th>Questionário</Th>
        <Th>Empresa</Th>
        <Th>Prazo de resposta</Th>
        <Th>Status</Th>
        <Th isNumeric>Qtd. questões</Th>
        <Th>Auditor vinculado</Th>
        <Th>Visualizar</Th>
       </Tr>
      </Thead>
      <Tbody>
       {pagedRows.map((row) => (
        <Tr key={row.id}>
         <Td>
          <Badge colorScheme={TYPE_COLORS[row.type] || 'gray'} textTransform='none'>
           {TYPE_LABELS[row.type] || row.type}
          </Badge>
         </Td>
         <Td>{row.name}</Td>
         <Td>{row.company_name}</Td>
         <Td>{formatDeadline(row.end_application_date)}</Td>
         <Td>
          <Flex direction='column' gap='2px'>
           <Text fontSize='sm'>{row.status}</Text>
           {row.type !== 'certification' && (
            <Text fontSize='xs' color={row.is_answered ? 'green.600' : '#818A8F'}>
             {row.is_answered ? 'respondido' : 'sem resposta'}
            </Text>
           )}
          </Flex>
         </Td>
         <Td isNumeric>{row.questions_count}</Td>
         <Td>
          <Box minW='190px'>
           {/* Sem "Atribuir a mim": só quem tem papel AVALIADOR pode ser
               vinculado, então para um MASTER a opção criava um vínculo que a
               coluna nem conseguia exibir. */}
           <FilterCombobox
            size='sm'
            value={row.auditor?.id_user ?? ''}
            options={auditorOptions}
            onChange={(value) => assignAuditor(row, value === '' ? null : value)}
            placeholder='Nenhum'
            searchPlaceholder='Buscar auditor'
            emptyLabel='Nenhum auditor encontrado'
            clearLabel='Nenhum'
            isLoading={savingRowId === row.id}
           />
          </Box>
         </Td>
         <Td>
          {/* Abre na mesma aba: a tela de auditoria tem "Voltar para a lista". */}
          <Tooltip
           label={row.audit_survey_id ? '' : 'Questionário sem survey vinculado'}
           isDisabled={!!row.audit_survey_id}
           hasArrow
          >
           <Button
            size='sm'
            variant='primary'
            isDisabled={!row.audit_survey_id}
            onClick={() => openAudit(row)}
           >
            Visualizar
           </Button>
          </Tooltip>
         </Td>
        </Tr>
       ))}
      </Tbody>
     </Table>

     {!isLoading && totalRows === 0 && (
      <Text px='16px' py='24px' color='#818A8F'>
       Nenhum questionário para estes filtros.
      </Text>
     )}

     {!isLoading && totalRows > 0 && (
      <Box px='16px' pb='8px'>
       <Pagination
        currentPage={page}
        onPageChange={setPage}
        meta={{ total: totalRows, last_page: lastPage }}
        viewing={pagedRows.length}
       />
      </Box>
     )}
    </TableContainer>
   )}
  </Box>
 );
}
